import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { createWorkerSchema, type CreateWorkerInput, type WorkerFormOptions } from "@crewledger/shared/schemas";
import { PageHeader } from "@/components/design";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createWorker, fetchWorker, fetchWorkerOptions, updateWorker } from "./api";
import { PrivacyHelper } from "./components/worker-status-badge";
import { useWorkerPermissions } from "./use-worker-permissions";

interface WorkerFormPageProps {
  mode: "create" | "edit";
}

export function WorkerFormPage({ mode }: WorkerFormPageProps) {
  const { workerId } = useParams();
  const navigate = useNavigate();
  const { canWrite, canViewPayroll } = useWorkerPermissions();
  const [options, setOptions] = useState<WorkerFormOptions | null>(null);
  const [loading, setLoading] = useState(mode === "edit");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<CreateWorkerInput>({
    resolver: zodResolver(createWorkerSchema),
    defaultValues: {
      employeeId: "",
      internalName: "",
      displayName: "",
      baseHourlyRate: 0,
      trade: "",
      crew: "",
      siteId: null,
      ruleProfileId: null,
      status: "ACTIVE",
      notes: "",
    },
  });

  useEffect(() => {
    fetchWorkerOptions()
      .then((res) => { setOptions(res.data); })
      .catch(() => { setOptions(null); });
  }, []);

  useEffect(() => {
    if (mode !== "edit" || !workerId) return;
    setLoading(true);
    fetchWorker(workerId)
      .then((res) => {
        const w = res.data;
        form.reset({
          employeeId: w.employeeId,
          internalName: w.internalName ?? "",
          displayName: w.displayName,
          baseHourlyRate: w.baseHourlyRate ?? 0,
          trade: w.trade ?? "",
          crew: w.crew ?? "",
          siteId: w.siteId,
          ruleProfileId: w.ruleProfileId,
          status: w.status,
          notes: w.notes ?? "",
        });
      })
      .catch((err: unknown) =>
        { setSubmitError(err instanceof Error ? err.message : "Failed to load worker"); },
      )
      .finally(() => { setLoading(false); });
  }, [mode, workerId, form]);

  if (!canWrite) {
    return (
      <PageHeader title="Access denied" description="You do not have permission to edit workers." />
    );
  }

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    const payload = {
      ...values,
      trade: values.trade?.trim() ? values.trade.trim() : null,
      crew: values.crew?.trim() ? values.crew.trim() : null,
      notes: values.notes?.trim() ? values.notes.trim() : null,
      siteId: values.siteId || null,
      ruleProfileId: values.ruleProfileId || null,
    };
    try {
      if (mode === "create") {
        const res = await createWorker(payload);
        navigate(`/workers/${res.data.id}`);
      } else if (workerId) {
        const res = await updateWorker(workerId, payload);
        navigate(`/workers/${res.data.id}`);
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to save worker");
    }
  });

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader
        title={mode === "create" ? "New worker" : "Edit worker"}
        description="Crew member profile, rate, and site assignment."
      />

      <PrivacyHelper />

      {loading ? (
        <div className="h-96 animate-pulse rounded-xl border bg-card-white" />
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-6 rounded-xl border bg-card-white p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="employeeId">Employee ID</Label>
              <Input id="employeeId" {...form.register("employeeId")} className="font-mono uppercase" />
              {form.formState.errors.employeeId ? (
                <p className="text-xs text-red-danger">{form.formState.errors.employeeId.message}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                {...form.register("status")}
                className="flex h-9 w-full rounded-md border border-input px-3 text-sm"
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="internalName">Internal name (payroll)</Label>
              <Input id="internalName" {...form.register("internalName")} />
              {form.formState.errors.internalName ? (
                <p className="text-xs text-red-danger">{form.formState.errors.internalName.message}</p>
              ) : null}
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="displayName">Display name</Label>
              <Input id="displayName" {...form.register("displayName")} />
            </div>
            {canViewPayroll ? (
              <div className="space-y-2">
                <Label htmlFor="baseHourlyRate">Base hourly rate ($)</Label>
                <Input
                  id="baseHourlyRate"
                  type="number"
                  step="0.01"
                  {...form.register("baseHourlyRate", { valueAsNumber: true })}
                />
              </div>
            ) : null}
            <div className="space-y-2">
              <Label htmlFor="trade">Trade</Label>
              <Input id="trade" list="trades" {...form.register("trade")} />
              <datalist id="trades">
                {options?.trades.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </div>
            <div className="space-y-2">
              <Label htmlFor="crew">Crew</Label>
              <Input id="crew" list="crews" {...form.register("crew")} />
              <datalist id="crews">
                {options?.crews.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
            <div className="space-y-2">
              <Label htmlFor="siteId">Site</Label>
              <select
                id="siteId"
                value={form.watch("siteId") ?? ""}
                onChange={(e) => { form.setValue("siteId", e.target.value || null); }}
                className="flex h-9 w-full rounded-md border border-input px-3 text-sm"
              >
                <option value="">Unassigned</option>
                {options?.sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code} — {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ruleProfileId">Pay rule profile</Label>
              <select
                id="ruleProfileId"
                value={form.watch("ruleProfileId") ?? ""}
                onChange={(e) => { form.setValue("ruleProfileId", e.target.value || null); }}
                className="flex h-9 w-full rounded-md border border-input px-3 text-sm"
              >
                <option value="">Default</option>
                {options?.payRuleProfiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                {...form.register("notes")}
                rows={3}
                className="w-full rounded-md border border-input px-3 py-2 text-sm"
              />
            </div>
          </div>

          {submitError ? <p className="text-sm text-red-danger">{submitError}</p> : null}

          <div className="flex gap-2">
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Saving…" : mode === "create" ? "Create worker" : "Save changes"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(mode === "edit" && workerId ? `/workers/${workerId}` : "/workers")}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

export function WorkerCreatePage() {
  return <WorkerFormPage mode="create" />;
}

export function WorkerEditPage() {
  return <WorkerFormPage mode="edit" />;
}
